import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from "@headlessui/react";
import { BoardPage as BoardPageType } from '../../../stores/boardStore';

interface BoardPaginationProps {
    pages: BoardPageType[];
    activePageId: string | null;
    onSelectPage: (pageId: string) => void;
    onAddPage: () => void;
    isLoading?: boolean;
    // Page Management Props
    onUpdatePage: (pageId: string, name: string) => void;
    onDeletePage: (pageId: string) => void;
    onReorderPages: (orderedPageIds: string[]) => void;
}

export const BoardPagination: React.FC<BoardPaginationProps> = ({
    pages,
    activePageId,
    onSelectPage,
    onAddPage,
    isLoading = false,
    onUpdatePage,
    onDeletePage,
    onReorderPages
}) => {
    const { t } = useTranslation('common');



    // Menu State - tracks which menu is open by ID ('MAIN' or pageId)
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ bottom: number, right?: number, left?: number } | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Page Editing State
    const [editingPageId, setEditingPageId] = useState<string | null>(null);
    const [tempPageName, setTempPageName] = useState("");
    const pageInputRef = useRef<HTMLInputElement>(null);

    // ========================
    // Drag and Drop State
    // ========================
    const [isDragging, setIsDragging] = useState(false);
    const [draggedPageId, setDraggedPageId] = useState<string | null>(null);
    const [recentlyDraggedPageId, setRecentlyDraggedPageId] = useState<string | null>(null);
    const [placeholderIndex, setPlaceholderIndex] = useState<number | null>(null);

    // DOM refs for drag operations
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const pageTabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    // Drag Overlay ref
    const dragOverlayRef = useRef<HTMLDivElement>(null);
    const [dragOverlayRect, setDragOverlayRect] = useState<DOMRect | null>(null);
    const initialTabRectsRef = useRef<Map<string, DOMRect>>(new Map());
    const draggedTabWidthRef = useRef<number>(0);
    const dragStartXRef = useRef<number>(0);
    const initialScrollLeftRef = useRef<number>(0); // Track scroll position at drag start

    // Timer/interval refs
    const autoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const scrollDirectionRef = useRef<'left' | 'right' | null>(null);
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pendingDragRef = useRef<{
        pageId: string;
        pageIndex: number;
        startX: number;
        startY: number;
        isTouch: boolean;
        pointerId?: number;
    } | null>(null);

    // Drag state ref (avoids stale closures in event handlers)
    const dragStateRef = useRef<{
        isDragging: boolean;
        draggedPageId: string | null;
        originalIndex: number | null;
        placeholderIndex: number | null;
    }>({
        isDragging: false,
        draggedPageId: null,
        originalIndex: null,
        placeholderIndex: null
    });

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Check if click is outside the currently open menu
            if (isMenuVisible && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuVisible(false);
            }
        };

        // Handle scrolling closes menu (to avoid floating menu in wrong place)
        const handleScroll = () => {
            if (isMenuVisible) setIsMenuVisible(false);
        };

        document.addEventListener('click', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true); // Capture scroll on any element
        return () => {
            document.removeEventListener('click', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);

    // Handle Page Renaming Focus
    useEffect(() => {
        if (editingPageId && pageInputRef.current) {
            setTimeout(() => {
                if (pageInputRef.current) {
                    pageInputRef.current.focus();
                    pageInputRef.current.select();
                }
            }, 0);
        }
    }, [editingPageId]);

    const handleMenuToggle = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();

        if (openMenuId === id && isMenuVisible) {
            setIsMenuVisible(false);
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const MENU_WIDTH = 128; // w-32 approx 128px
        const SCREEN_PADDING = 8;

        const newPosition: { bottom: number, right?: number, left?: number } = {
            bottom: window.innerHeight - rect.top + 5,
        };

        // Determine horizontal position
        // Check if there is space on the left side of the button (for right-anchored menu)
        // Menu goes from (rect.right - MENU_WIDTH) to rect.right
        if (rect.right - MENU_WIDTH >= SCREEN_PADDING) {
            // Enough space on left, anchor to right
            newPosition.right = window.innerWidth - rect.right;
        } else {
            // Not enough space on left, anchor to left
            newPosition.left = rect.left;
        }

        setMenuPosition(newPosition);
        setOpenMenuId(id);
        setIsMenuVisible(true);
    };



    const handlePageRenameStart = (e: React.MouseEvent, page: BoardPageType) => {
        e.stopPropagation();
        setEditingPageId(page.id);
        setTempPageName(page.name);
        setIsMenuVisible(false);
    };

    const handlePageDeleteClick = (e: React.MouseEvent, pageId: string) => {
        e.stopPropagation();
        setIsMenuVisible(false);
        onDeletePage(pageId);
    };

    const handlePageNameSubmit = () => {
        if (editingPageId && tempPageName.trim()) {
            onUpdatePage(editingPageId, tempPageName.trim());
        }

        setEditingPageId(null);
    };

    const handlePageNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
        } else if (e.key === 'Escape') {
            setEditingPageId(null);
        }
    };

    // Helper to get active page for menu
    const activeMenuPage = openMenuId && openMenuId !== 'MAIN'
        ? pages.find(p => p.id === openMenuId)
        : null;

    // ========================
    // Drag and Drop Handlers
    // ========================

    // Keep refs in sync with props to avoid stale closures
    const pagesRef = useRef(pages);
    pagesRef.current = pages;
    const onReorderPagesRef = useRef(onReorderPages);
    onReorderPagesRef.current = onReorderPages;

    const stopAutoScroll = useCallback(() => {
        if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
            autoScrollIntervalRef.current = null;
        }
        scrollDirectionRef.current = null;
    }, []);

    const clearLongPressTimer = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const finishDrag = useCallback(() => {
        const { isDragging: wasDragging, draggedPageId: pageId, originalIndex, placeholderIndex: targetIndex } = dragStateRef.current;
        const currentPages = pagesRef.current;

        if (wasDragging && pageId && targetIndex !== null && originalIndex !== null) {
            if (originalIndex !== targetIndex) {
                const newOrder = [...currentPages];
                const [movedPage] = newOrder.splice(originalIndex, 1);
                newOrder.splice(targetIndex, 0, movedPage);
                onReorderPagesRef.current(newOrder.map(p => p.id));
            }
        }

        // Reset drag state
        dragStateRef.current = {
            isDragging: false,
            draggedPageId: null,
            originalIndex: null,
            placeholderIndex: null
        };
        pendingDragRef.current = null;

        setIsDragging(false);
        setRecentlyDraggedPageId(pageId); // Set recent drag for effect
        setTimeout(() => setRecentlyDraggedPageId(null), 200); // Clear after 200ms

        setDraggedPageId(null);
        setPlaceholderIndex(null);
        setDragOverlayRect(null);

        if (dragOverlayRef.current) {
            dragOverlayRef.current.style.transform = '';
        }
        initialTabRectsRef.current.clear();

        stopAutoScroll();
    }, [stopAutoScroll]);

    const updateDragPosition = useCallback((clientX: number) => {
        if (!dragStateRef.current.isDragging || !scrollContainerRef.current || !dragOverlayRef.current) return;

        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();

        // Move the OVERLAY, not the list item
        // Calculate delta for the overlay (fixed/absolute)
        // We want the overlay to track the mouse.
        // The startX was relative to the viewport.
        const deltaX = clientX - dragStartXRef.current;

        // We apply translate to the overlay. 
        dragOverlayRef.current.style.transform = `translateX(${deltaX}px) scale(1.05)`;

        // Auto-scroll at edges
        const SCROLL_ZONE = 40;
        const SCROLL_SPEED = 6;
        const maxScroll = container.scrollWidth - container.clientWidth;

        const isInLeftZone = clientX < containerRect.left + SCROLL_ZONE;
        const isInRightZone = clientX > containerRect.right - SCROLL_ZONE;

        if (!isInLeftZone && !isInRightZone) {
            stopAutoScroll();
        }
        else if (isInLeftZone) {
            if (scrollDirectionRef.current !== 'left') stopAutoScroll();
            if (container.scrollLeft <= 1) stopAutoScroll();
            else if (!autoScrollIntervalRef.current) {
                scrollDirectionRef.current = 'left';
                autoScrollIntervalRef.current = setInterval(() => {
                    if (!scrollContainerRef.current) return;
                    if (scrollContainerRef.current.scrollLeft <= 1) {
                        scrollContainerRef.current.scrollLeft = 0;
                        stopAutoScroll();
                        return;
                    }
                    scrollContainerRef.current.scrollLeft -= SCROLL_SPEED;
                }, 16);
            }
        }
        else if (isInRightZone) {
            if (scrollDirectionRef.current !== 'right') stopAutoScroll();
            if (container.scrollLeft >= maxScroll - 1) stopAutoScroll();
            else if (!autoScrollIntervalRef.current) {
                scrollDirectionRef.current = 'right';
                autoScrollIntervalRef.current = setInterval(() => {
                    if (!scrollContainerRef.current) return;
                    const currentMax = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth;
                    if (scrollContainerRef.current.scrollLeft >= currentMax - 1) {
                        scrollContainerRef.current.scrollLeft = currentMax;
                        stopAutoScroll();
                        return;
                    }
                    scrollContainerRef.current.scrollLeft += SCROLL_SPEED;
                }, 16);
            }
        }

        // Center-point crossing detection
        const currentPages = pagesRef.current;
        const draggedId = dragStateRef.current.draggedPageId;
        const currentPlaceholder = dragStateRef.current.placeholderIndex ?? 0;
        const originalIndex = dragStateRef.current.originalIndex ?? 0;

        // Calculate the current scroll offset relative to start
        // If scrollLeft increased, items moved left visually, so their rect.left should decrease.
        const currentScrollLeft = container.scrollLeft;
        const initialScrollLeft = initialScrollLeftRef.current;
        const scrollDiff = currentScrollLeft - initialScrollLeft;

        // The overlay position is purely viewport based (clientX)
        // We want to compare the overlay center with the CURRENT center of the tabs.
        // draggedOriginalRect.left is the initial viewport left.
        // deltaX is movement from start.
        const draggedOriginalRect = initialTabRectsRef.current.get(draggedId || '');
        if (!draggedOriginalRect) return;

        // Visual edges of the dragged item
        const draggedLeft = draggedOriginalRect.left + deltaX;
        const draggedRight = draggedLeft + draggedTabWidthRef.current;

        let passedCount = 0;

        for (let i = 0; i < currentPages.length; i++) {
            const page = currentPages[i];
            if (page.id === draggedId) continue;

            const tabRect = initialTabRectsRef.current.get(page.id);
            if (!tabRect) continue;

            // Adjust tab center for scroll
            const currentTabLeft = tabRect.left - scrollDiff;
            const tabCenter = currentTabLeft + (tabRect.width / 2);

            if (i < originalIndex) {
                // Item is originally to the left.
                // We are "after" it (passedCount++) unless we moved left past its center.
                if (draggedLeft >= tabCenter) {
                    passedCount++;
                }
            } else {
                // Item is originally to the right.
                // We are "after" it (passedCount++) only if we moved right past its center.
                if (draggedRight > tabCenter) {
                    passedCount++;
                }
            }
        }

        let newPlaceholderIndex = passedCount;
        if (passedCount > originalIndex) {
            newPlaceholderIndex = Math.min(passedCount, currentPages.length - 1);
        }

        if (newPlaceholderIndex !== currentPlaceholder) {
            dragStateRef.current.placeholderIndex = newPlaceholderIndex;
            setPlaceholderIndex(newPlaceholderIndex);
        }
    }, [stopAutoScroll]);

    const startDrag = useCallback((pageId: string, pageIndex: number, startX: number) => {
        initialTabRectsRef.current.clear();
        pageTabRefs.current.forEach((el, id) => {
            initialTabRectsRef.current.set(id, el.getBoundingClientRect());
        });

        const draggedEl = pageTabRefs.current.get(pageId);
        if (draggedEl) {
            const rect = draggedEl.getBoundingClientRect();
            draggedTabWidthRef.current = rect.width;
            setDragOverlayRect(rect);
        }

        dragStateRef.current = {
            isDragging: true,
            draggedPageId: pageId,
            originalIndex: pageIndex,
            placeholderIndex: pageIndex
        };
        dragStartXRef.current = startX;

        if (scrollContainerRef.current) {
            initialScrollLeftRef.current = scrollContainerRef.current.scrollLeft;
        }

        // We no longer rely on draggedElementRef pointing to the list item moving
        // We use dragOverlayRef for movement.

        setIsDragging(true);
        setDraggedPageId(pageId);
        setPlaceholderIndex(pageIndex);
    }, []);

    // --- Unified Pointer Handlers (Mouse & Touch) ---
    // This replaces separate mouse/touch logic to avoid race conditions and provide robust mobile support

    const handlePointerDown = useCallback((e: React.PointerEvent, pageId: string, pageIndex: number) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        clearLongPressTimer();

        // Robust touch detection: Check pointer type, coarse pointer, or lack of hover
        const isTouch = e.pointerType !== 'mouse' ||
            (typeof window !== 'undefined' && (
                window.matchMedia('(pointer: coarse)').matches ||
                window.matchMedia('(hover: none)').matches
            ));

        pendingDragRef.current = {
            pageId,
            pageIndex,
            startX: e.clientX,
            startY: e.clientY,
            isTouch,
        };

        if (isTouch) {
            // Touch/Tablet: Wait for long press to initiate drag
            longPressTimerRef.current = setTimeout(() => {
                if (pendingDragRef.current?.pageId === pageId) {
                    try {
                        const element = pageTabRefs.current.get(pageId);
                        element?.setPointerCapture(e.pointerId);
                    } catch (err) { /* ignore */ }
                    startDrag(pageId, pageIndex, e.clientX);
                }
            }, 300);
        }
        // Mouse: Do nothing yet. Wait for movement threshold.
    }, [clearLongPressTimer, startDrag]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (dragStateRef.current.isDragging) {
            e.preventDefault();
            updateDragPosition(e.clientX);
            return;
        }

        const pending = pendingDragRef.current;
        if (!pending) return;

        // Ensure buttons are pressed for mouse
        if (!pending.isTouch && e.buttons === 0) {
            pendingDragRef.current = null;
            return;
        }

        const deltaX = Math.abs(e.clientX - pending.startX);
        const deltaY = Math.abs(e.clientY - pending.startY);
        const hasMoved = deltaX > 5 || deltaY > 5;

        if (hasMoved) {
            if (pending.isTouch) {
                // Touch moved before timer -> It's a scroll, cancel drag potential
                clearLongPressTimer();
                pendingDragRef.current = null;
            } else {
                // Mouse moved > 5px -> Start drag immediately
                try {
                    e.currentTarget.setPointerCapture(e.pointerId);
                } catch (err) { }
                startDrag(pending.pageId, pending.pageIndex, pending.startX);
            }
        }
    }, [startDrag, updateDragPosition, clearLongPressTimer]);

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        clearLongPressTimer();

        if (dragStateRef.current.isDragging) {
            finishDrag();
        }

        pendingDragRef.current = null;

        // Release capture if we had it
        try {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
        } catch (err) {
            // ignore
        }
    }, [clearLongPressTimer, finishDrag]);

    // Handle cancellation (e.g. browser takes over for scroll)
    const handlePointerCancel = useCallback(() => {
        clearLongPressTimer();

        if (dragStateRef.current.isDragging) {
            finishDrag();
        }

        pendingDragRef.current = null;
    }, [clearLongPressTimer, finishDrag]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearLongPressTimer();
            stopAutoScroll();
        };
    }, [clearLongPressTimer, stopAutoScroll]);

    return (
        <div className="flex justify-center w-full py-2 z-40">
            {/* Drag Overlay Portal/Fixed Element */}
            {isDragging && draggedPageId && dragOverlayRect && (
                (() => {
                    const draggedPage = pages.find(p => p.id === draggedPageId);
                    const isActive = draggedPageId === activePageId;
                    return (
                        <div
                            ref={dragOverlayRef}
                            className={`fixed z-[60] pointer-events-none flex items-center gap-2 pl-3 pr-8 py-1.5 rounded-md text-xs font-medium shadow-xl ring-2 ring-accent-primary opacity-95 backdrop-blur-sm bg-white dark:bg-neutral-900 ${isActive ? 'text-accent-primary' : 'text-neutral-500 dark:text-neutral-400'
                                }`}
                            style={{
                                top: dragOverlayRect.top,
                                left: dragOverlayRect.left,
                                width: dragOverlayRect.width,
                                height: dragOverlayRect.height,
                            }}
                        >
                            <span className="truncate max-w-[100px]">
                                {draggedPage?.name}
                            </span>
                            {/* Previous implementation HID the underline during drag, so we omit it here to match 'previous highlight effect' exactly. 
                                If the user Wants the underline, we can uncomment. But 'previous' implies matching the old 'isBeingDragged' state where it was hidden. */}
                        </div>
                    );
                })()
            )}

            <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-lg shadow-2xl p-1 flex items-center gap-1 transition-all ring-1 ring-black/5 dark:ring-white/10 max-w-[90vw] md:max-w-2xl">



                {/* Scrollable Pages */}
                {pages.length > 0 && (
                    <div
                        ref={scrollContainerRef}
                        className="flex-1 min-w-0 flex items-center gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] py-2 -my-2 pl-1 pr-1"
                    >
                        {pages.map((page, index) => {
                            const isPageActive = page.id === activePageId;
                            const isEditingThisPage = editingPageId === page.id;
                            const isDraggedItem = isDragging && draggedPageId === page.id;

                            // Calculate shift/transform logic
                            const originalIdx = dragStateRef.current.originalIndex; // where the dragged item IS physically
                            const placeholderIdx = placeholderIndex; // where the gap visual IS

                            const shiftAmount = draggedTabWidthRef.current + 4;
                            let shiftPx = 0;

                            if (isDragging && !isDraggedItem && placeholderIdx !== null && originalIdx !== null) {
                                if (placeholderIdx !== originalIdx) {
                                    // Standard neighbor shift logic
                                    if (placeholderIdx > originalIdx && index > originalIdx && index <= placeholderIdx) {
                                        shiftPx = -shiftAmount;
                                    }
                                    else if (placeholderIdx < originalIdx && index >= placeholderIdx && index < originalIdx) {
                                        shiftPx = shiftAmount;
                                    }
                                }
                            } else if (isDragging && isDraggedItem && placeholderIdx !== null && originalIdx !== null) {
                                // Placeholder Shift Logic: The placeholder (original item) needs to JUMP to the gap
                                if (placeholderIdx !== originalIdx) {
                                    // Calculate distance to jump
                                    let dist = 0;
                                    // We iterate through the items between original and placeholder
                                    if (placeholderIdx > originalIdx) {
                                        // Moving Right: Sum widths of items we are jumping over
                                        if (originalIdx !== null && placeholderIdx !== null) {
                                            for (let i = originalIdx + 1; i <= placeholderIdx; i++) {
                                                const pageId = pages[i]?.id;
                                                if (pageId) {
                                                    const rect = initialTabRectsRef.current.get(pageId);
                                                    if (rect) dist += (rect.width + 4);
                                                }
                                            }
                                        }
                                        shiftPx = dist;
                                    } else {
                                        // Moving Left
                                        if (originalIdx !== null && placeholderIdx !== null) {
                                            for (let i = placeholderIdx; i < originalIdx; i++) {
                                                const pageId = pages[i]?.id;
                                                if (pageId) {
                                                    const rect = initialTabRectsRef.current.get(pageId);
                                                    if (rect) dist -= (rect.width + 4);
                                                }
                                            }
                                        }
                                        shiftPx = dist;
                                    }
                                }
                            }

                            // Determine styles
                            let contentClass = `pl-3 pr-8 py-1.5 rounded-md text-xs font-medium relative overflow-hidden flex items-center gap-2 cursor-pointer select-none transition-colors duration-150 touch-pan-x`;

                            // Placeholder styling vs Normal vs Recently Dragged
                            if (isDraggedItem) {
                                contentClass += ` border border-dashed border-neutral-300 dark:border-neutral-700 bg-neutral-100/50 dark:bg-white/5 text-transparent`;
                            } else if (recentlyDraggedPageId === page.id) {
                                // Recently dragged - Keep highlight for a moment
                                contentClass += ` shadow-xl ring-2 ring-accent-primary opacity-95 bg-white dark:bg-neutral-800 ${isPageActive ? 'text-accent-primary' : 'text-neutral-500 dark:text-neutral-400'}`;
                            } else {
                                contentClass += isPageActive
                                    ? ' text-accent-primary bg-accent-primary/10 shadow-sm'
                                    : ' text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white hover:bg-neutral-100/50 dark:hover:bg-white/5';
                            }

                            return (
                                <div
                                    className={`relative group flex-shrink-0 ${isDraggedItem ? 'z-0' : ''} ${isDragging ? 'transition-transform duration-200 ease-out' : ''}`}
                                    key={page.id}
                                    ref={(el) => {
                                        if (el) pageTabRefs.current.set(page.id, el);
                                        else pageTabRefs.current.delete(page.id);
                                    }}
                                    style={{
                                        transform: shiftPx !== 0 ? `translateX(${shiftPx}px)` : undefined,
                                        willChange: isDragging ? 'transform' : undefined,
                                    }}
                                >
                                    <div
                                        onClick={() => !isDragging && onSelectPage(page.id)}
                                        onPointerDown={(e) => handlePointerDown(e, page.id, index)}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={handlePointerUp}
                                        onPointerCancel={handlePointerCancel}
                                        onDoubleClick={(e) => handlePageRenameStart(e, page)}
                                        onContextMenu={(e) => e.preventDefault()}
                                        className={contentClass}
                                    >
                                        {isEditingThisPage ? (
                                            <input
                                                ref={pageInputRef}
                                                type="text"
                                                name="page-name-field"
                                                autoComplete="off"
                                                data-1p-ignore
                                                data-lpignore="true"
                                                data-bwignore="true"
                                                data-form-type="other"
                                                value={tempPageName}
                                                onChange={(e) => setTempPageName(e.target.value)}
                                                onKeyDown={handlePageNameKeyDown}
                                                onBlur={handlePageNameSubmit}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-20 bg-transparent border-none outline-none p-0 text-xs font-semibold text-neutral-900 dark:text-neutral-100 focus:ring-0"
                                            />
                                        ) : (
                                            <span className={`truncate max-w-[100px] ${isDraggedItem ? 'opacity-0' : ''}`}>{page.name}</span>
                                        )}

                                        {/* Active indicator (not on placeholder) */}
                                        {isPageActive && !isEditingThisPage && !isDraggedItem && (
                                            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-accent-primary opacity-50"></span>
                                        )}

                                        {/* Three dots (hidden on drag target) */}
                                        {!isDragging && (
                                            <button
                                                onClick={(e) => handleMenuToggle(e, page.id)}
                                                className={`absolute right-1 top-1/2 -translate-y-1/2 transition-opacity ${openMenuId === page.id ? 'opacity-100' : ''}`}
                                            >
                                                <span className="w-3.5 h-3.5 text-current" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a8868352.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a8868352.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}



                <button
                    id="onboarding-add-page"
                    onClick={onAddPage}
                    disabled={isLoading}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-neutral-500 hover:text-accent-primary dark:text-neutral-400 dark:hover:text-accent-primary hover:bg-accent-primary/5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group flex-shrink-0"
                    title={t("boards.addPage") || "Add Page"}
                >
                    <span className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90 group-hover:scale-110" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9e9d3e57.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9e9d3e57.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </button>
            </div>

            {/* Menu Dropdown - Rendered using Fixed Positioning */}
            <Transition
                show={isMenuVisible}
                as={React.Fragment}
                afterLeave={() => setOpenMenuId(null)}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        bottom: menuPosition?.bottom,
                        right: menuPosition?.right,
                        left: menuPosition?.left
                    }}
                    className={`z-50 w-32 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl py-1 overflow-hidden ${!openMenuId ? 'hidden' : ''}`}
                >
                    {activeMenuPage ? (
                        <>
                            <button
                                onClick={(e) => handlePageRenameStart(e, activeMenuPage)}
                                className="w-full text-left px-3 py-2 text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            >
                                {t('actions.rename')}
                            </button>
                            <button
                                onClick={(e) => handlePageDeleteClick(e, activeMenuPage.id)}
                                className="w-full text-left px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                                {t('actions.delete')}
                            </button>
                        </>
                    ) : null}
                </div>
            </Transition >
        </div >
    );
};

